import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, ArrowRight, Plus, Package, CalendarCheck, ShoppingBag, Brain, MessageSquare } from 'lucide-react';
import { useAdminStats } from '@/hooks/useAdminData';
import { GateAnalytics } from '@/components/admin/GateAnalytics';
import { useQuery } from '@tanstack/react-query';
import { membershipService } from '@/data/membership';

const AdminDashboard = () => {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const { data: stats, isLoading: loadingStats } = useAdminStats();
  
  const { data: usageStats } = useQuery({
    queryKey: ['admin-askcircle-usage'],
    queryFn: () => membershipService.getTotalUsageStats(),
    enabled: isAdmin,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  if (loading || !isAdmin) {
    return null;
  }

  const adminTools = [
    {
      title: 'Add New Product',
      description: 'Create a new product or service listing',
      icon: Plus,
      path: '/admin/add-product',
      color: 'text-primary',
    },
    {
      title: 'User Management',
      description: 'Manage user roles, pro memberships, and permissions',
      icon: Users,
      path: '/admin/users',
      color: 'text-blue-500',
    },
    {
      title: 'AI Training',
      description: 'Review conversations, rate responses, and train AskCircle AI',
      icon: Brain,
      path: '/admin/ai-training',
      color: 'text-purple-600',
    },
    {
      title: 'Gear Bundles',
      description: 'Manage gear bundle kits, items, and bulk imports',
      icon: ShoppingBag,
      path: '/admin/bundles',
      color: 'text-green-500',
    },
    {
      title: 'Vendor Updates',
      description: 'Review and approve vendor product updates',
      icon: Package,
      path: '/admin/vendor-updates',
      color: 'text-purple-500',
    },
    {
      title: 'Partner Applications',
      description: 'Review vendor applications and approve new partners',
      icon: CalendarCheck,
      path: '/admin/partner-applications',
      color: 'text-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              Manage users, products, and system settings
            </p>
          </div>

          {/* Admin Tools Grid */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Admin Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {adminTools.map((tool) => (
                <Card key={tool.path} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(tool.path)}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <tool.icon className={`h-8 w-8 ${tool.color}`} />
                        <div>
                          <CardTitle className="text-xl">{tool.title}</CardTitle>
                          <CardDescription className="mt-1">
                            {tool.description}
                          </CardDescription>
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Total Users</CardDescription>
                <CardTitle className="text-3xl">
                  {loadingStats ? '...' : stats?.totalUsers ?? 0}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Products
                </CardDescription>
                <CardTitle className="text-3xl">
                  {loadingStats ? '...' : stats?.totalProducts ?? 0}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription className="flex items-center gap-2">
                  <CalendarCheck className="h-4 w-4" />
                  Services
                </CardDescription>
                <CardTitle className="text-3xl">
                  {loadingStats ? '...' : stats?.totalServices ?? 0}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Memberships</CardDescription>
                <CardTitle className="text-3xl">
                  {loadingStats ? '...' : stats?.totalMemberships ?? 0}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Founder Members</CardDescription>
                <CardTitle className="text-3xl">
                  {loadingStats ? '...' : stats?.founderCount ?? 0}
                  <span className="text-base text-muted-foreground ml-1">/ 500</span>
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* AskCircle Usage Stats */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">AskCircle Usage</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Today's Messages
                  </CardDescription>
                  <CardTitle className="text-3xl">
                    {usageStats?.todayMessages || 0}
                  </CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Last 30 Days</CardDescription>
                  <CardTitle className="text-3xl">
                    {usageStats?.totalMessages || 0}
                  </CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Free Tier</CardDescription>
                  <CardTitle className="text-3xl">
                    {usageStats?.byTier.free || 0}
                  </CardTitle>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardDescription>Pro/Founder</CardDescription>
                  <CardTitle className="text-3xl">
                    {(usageStats?.byTier.pro || 0) + (usageStats?.byTier.founder || 0)}
                  </CardTitle>
                </CardHeader>
              </Card>
            </div>
          </div>

          {/* Gate Analytics */}
          <div className="mb-12">
            <GateAnalytics />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
